import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useTran } from "@/hooks";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toJS } from "mobx";

const COLORS = [
  "#10b981",
  "#3b82f6",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
];

const CarbonPerformanceCharts = ({ performance }) => {
  const { t } = useTran();
  // Safety check for required props
  if (!performance || !performance.scope1 || !performance.scope2) {
    return null;
  }

  // Bar chart data: Scope comparison
  const scopeData = [
    {
      name: "Scope 1",
      Target: Number(performance.scope1.target.toFixed(2)),
      Actual: Number(performance.scope1.actual.toFixed(2)),
      unit: "tCO₂e",
    },
    {
      name: "Scope 2",
      Target: Number(performance.scope2.target.toFixed(2)),
      Actual: Number(performance.scope2.actual.toFixed(2)),
      unit: "tCO₂e",
    },
  ];

  // Pie chart data: SME contribution distribution (only usable submissions)
  const pieData = (performance.smeContributions || [])
    // .filter((sme) => sme.isUsable)
    .map((sme) => ({
      name: sme.smeName,
      value: sme.scope1 + sme.scope2,
      scope1: sme.scope1,
      scope2: sme.scope2,
    }))
    .filter((item) => item.value > 0) // Only show SMEs with data
    .sort((a, b) => b.value - a.value); // Sort by contribution descending
  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const unit = payload[0].payload.unit;
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <p className="mb-2 font-semibold text-slate-900 dark:text-white">
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()} {unit}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <p className="mb-2 font-semibold text-slate-900 dark:text-white">
            {data.name}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Total: {data.value.toFixed(2)} tCO₂e
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Scope 1: {data.scope1.toFixed(2)} tCO₂e
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Scope 2: {data.scope2.toFixed(2)} tCO₂e
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.05) return null; // Don't show label if slice is too small
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Bar Chart: Target vs Actual */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-base">
            {t("Target vs Actual Comparison")}
          </CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("Compare emission targets against actual SME submissions")}
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={scopeData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-slate-200 dark:stroke-slate-700"
              />
              <XAxis
                dataKey="name"
                tick={{ fill: "currentColor" }}
                className="text-slate-600 dark:text-slate-400"
              />
              <YAxis
                tick={{ fill: "currentColor" }}
                className="text-slate-600 dark:text-slate-400"
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Bar
                dataKey="Target"
                fill="#3b82f6"
                name="Target"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="Actual"
                fill="#10b981"
                name="Actual"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Pie Chart: SME Contribution Distribution */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-base">
            {t("SME Contribution Distribution")}
          </CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("Breakdown of approved emissions by organization unit")}
          </p>
        </CardHeader>
        <CardContent>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  formatter={(value, entry) => {
                    const percentage = (
                      (entry.payload.value /
                        pieData.reduce((sum, d) => sum + d.value, 0)) *
                      100
                    ).toFixed(1);
                    return `${value} (${percentage}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-slate-500 dark:text-slate-400">
              <p className="text-sm">
                {t("No approved submissions with data yet")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CarbonPerformanceCharts;
